// src/pages/ELearning/UserProfile.tsx — DESIGN v2
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/client';
import {
  Award, TrendingUp, Calendar, CheckCircle, Clock, XCircle,
  Flame, Gift, User as UserIcon, Mail, Star, Target, Zap
} from 'lucide-react';

interface TestResult {
  _id: string;
  test: { _id: string; title: string };
  score: number;
  passed: boolean;
  createdAt: string;
}

const getInitials = (name?: string): string => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

const GRADIENT_HERO = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const UserProfile: React.FC = () => {
  const { user } = useAuth();
  const [recentResults, setRecentResults] = useState<TestResult[]>([]);
  const [loadingResults, setLoadingResults] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = React.useRef<HTMLInputElement>(null);

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Файл слишком большой (макс 5 МБ)');
      return;
    }
    setUploadingAvatar(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result as string;
        await api.put('/users/me', { photo: base64 });
        alert('Аватарка обновлена! Обновите страницу');
        window.location.reload();
      } catch (err: any) {
        console.error('Avatar upload error:', err);
        alert('Ошибка: ' + (err.response?.data?.error || err.message));
      } finally {
        setUploadingAvatar(false);
      }
    };
    reader.onerror = () => {
      alert('Ошибка чтения файла');
      setUploadingAvatar(false);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const fetchRecentResults = async () => {
      try {
        const res = await api.get('/elearning/results?limit=8');
        setRecentResults(res.data);
      } catch (err) {
        console.error('Ошибка загрузки результатов', err);
      } finally {
        setLoadingResults(false);
      }
    };
    fetchRecentResults();
  }, []);

  const lastReset = user?.elearning?.lastResetAt
    ? new Date(user.elearning.lastResetAt)
    : new Date();
  const nextResetDate = new Date(lastReset.getTime() + 6 * 30 * 24 * 60 * 60 * 1000);

  const rating = user?.elearning?.rating ?? 0;
  const points = user?.elearning?.totalPoints ?? 0;
  const streak = user?.elearning?.streak ?? 0;
  const completedCount = user?.elearning?.completedTests?.length ?? 0;

  // Уровень по баллам (условный)
  const level = Math.floor(points / 100) + 1;
  const pointsToNextLevel = (level * 100) - points;
  const levelProgress = ((points % 100) / 100) * 100;

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .up-hero { animation: fadeUp 0.5s ease-out; }
        .up-kpi { transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1); animation: fadeUp 0.5s ease-out backwards; }
        .up-kpi:hover { transform: translateY(-6px); box-shadow: 0 20px 50px rgba(31, 38, 135, 0.15) !important; }
        .up-kpi:nth-child(1) { animation-delay: 0.05s; }
        .up-kpi:nth-child(2) { animation-delay: 0.1s; }
        .up-kpi:nth-child(3) { animation-delay: 0.15s; }
        .up-kpi:nth-child(4) { animation-delay: 0.2s; }
        .up-kpi:nth-child(5) { animation-delay: 0.25s; }
        .up-card { transition: all 0.3s ease; animation: fadeUp 0.5s ease-out backwards; }
        .up-card:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(31, 38, 135, 0.1) !important; }
        .up-avatar { animation: float 4s ease-in-out infinite; }
      `}</style>

      <div style={styles.container}>
        {/* ═══ HERO ═══ */}
        <div className="up-hero" style={styles.hero}>
          <div style={styles.heroBg} />
          <div style={styles.heroContent}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {user?.photo ? (
                <img
                  src={user.photo}
                  alt={user.name || 'Avatar'}
                  className="up-avatar"
                  style={styles.avatarImg}
                />
              ) : (
                <div className="up-avatar" style={styles.avatar}>
                  {getInitials(user?.name)}
                </div>
              )}
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                style={styles.avatarEditBtn}
                title="Загрузить фото"
              >
                {uploadingAvatar ? '...' : '📷'}
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                style={{ display: 'none' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={styles.name}>{user?.name || 'Сотрудник'}</h1>
              <p style={styles.email}>
                <Mail size={14} /> {user?.email || '—'}
              </p>
              <div style={styles.heroBadges}>
                <span style={styles.roleBadge}>
                  <UserIcon size={12} /> {user?.role || 'employee'}
                </span>
                <span style={{ ...styles.roleBadge, background: 'rgba(246, 211, 101, 0.2)', color: '#d97706' }}>
                  <Star size={12} /> Уровень {level}
                </span>
              </div>
            </div>
          </div>

          {/* Прогресс до следующего уровня */}
          <div style={styles.levelBar}>
            <div style={styles.levelBarTop}>
              <span style={{ fontSize: 12, color: '#5a5f7a', fontWeight: 600 }}>
                <Zap size={12} style={{ display: 'inline', marginRight: 4 }} />
                До уровня {level + 1}: {pointsToNextLevel} баллов
              </span>
              <span style={{ fontSize: 12, color: '#667eea', fontWeight: 700 }}>
                {Math.round(levelProgress)}%
              </span>
            </div>
            <div style={styles.levelBarTrack}>
              <div style={{ ...styles.levelBarFill, width: `${levelProgress}%` }} />
            </div>
          </div>
        </div>

        {/* ═══ KPI ═══ */}
        <div style={styles.statsGrid}>
          <KPI
            icon={<Award size={22} color="#f59e0b" />}
            value={rating}
            label="Рейтинг"
            accent="linear-gradient(135deg, #f6d365 0%, #fda085 100%)"
            bg="rgba(245, 158, 11, 0.12)"
          />
          <KPI
            icon={<TrendingUp size={22} color="#10b981" />}
            value={points}
            label="Всего баллов"
            accent="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
            bg="rgba(16, 185, 129, 0.12)"
          />
          <KPI
            icon={<Flame size={22} color="#ef4444" />}
            value={streak}
            label="Дней в серии"
            accent="linear-gradient(135deg, #f5576c 0%, #f093fb 100%)"
            bg="rgba(239, 68, 68, 0.12)"
          />
          <KPI
            icon={<CheckCircle size={22} color="#667eea" />}
            value={completedCount}
            label="Тестов пройдено"
            accent="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            bg="rgba(102, 126, 234, 0.12)"
          />
          <KPI
            icon={<Calendar size={22} color="#8b5cf6" />}
            value={nextResetDate.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
            label="Сброс баллов"
            accent="linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)"
            bg="rgba(139, 92, 246, 0.12)"
          />
        </div>

        {/* ═══ ПОСЛЕДНИЕ РЕЗУЛЬТАТЫ ═══ */}
        <div style={styles.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={styles.sectionIcon}>
              <Target size={20} color="#fff" />
            </div>
            <h2 style={styles.sectionTitle}>Последние результаты</h2>
          </div>
          {!loadingResults && recentResults.length > 0 && (
            <span style={styles.counter}>{recentResults.length}</span>
          )}
        </div>

        {loadingResults ? (
          <div style={styles.loadingBlock}>
            <Clock size={20} color="#8b8fa8" />
            <span style={{ color: '#8b8fa8', marginLeft: 8 }}>Загрузка...</span>
          </div>
        ) : recentResults.length === 0 ? (
          <div style={styles.empty}>
            <Target size={48} color="#667eea" style={{ opacity: 0.4 }} />
            <h3 style={{ color: '#5a5f7a', marginTop: 12, fontWeight: 700 }}>
              Пока нет пройденных тестов
            </h3>
            <p style={{ color: '#8b8fa8', fontSize: 13 }}>
              Пройдите первый тест, чтобы увидеть результаты
            </p>
          </div>
        ) : (
          <div style={styles.resultsList}>
            {recentResults.map((r, idx) => {
              const passed = r.passed;
              return (
                <div
                  key={r._id}
                  className="up-card"
                  style={{ ...styles.resultItem, animationDelay: `${idx * 0.04}s` }}
                >
                  <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    background: passed
                      ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: passed
                      ? '0 6px 16px rgba(17, 153, 142, 0.3)'
                      : '0 6px 16px rgba(239, 68, 68, 0.3)',
                  }}>
                    {passed
                      ? <CheckCircle size={22} color="#fff" />
                      : <XCircle size={22} color="#fff" />}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.resultTitle}>{r.test?.title || 'Тест'}</div>
                    <div style={styles.resultMeta}>
                      <Calendar size={11} />
                      <span>{new Date(r.createdAt).toLocaleDateString('ru-RU')}</span>
                    </div>
                  </div>

                  <div style={{
                    textAlign: 'right',
                    flexShrink: 0,
                  }}>
                    <div style={{
                      fontSize: 20,
                      fontWeight: 800,
                      background: passed
                        ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                        : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      {r.score.toFixed(0)}%
                    </div>
                    <div style={{
                      fontSize: 10,
                      color: passed ? '#10b981' : '#ef4444',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}>
                      {passed ? 'Пройден' : 'Не сдан'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ ПОЛУЧЕННЫЕ БЕНЕФИТЫ ═══ */}
        <div style={styles.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{...styles.sectionIcon, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
              <Gift size={20} color="#fff" />
            </div>
            <h2 style={styles.sectionTitle}>Полученные бенефиты</h2>
          </div>
          {user?.elearning?.benefits?.length ? (
            <span style={styles.counter}>{user.elearning.benefits.length}</span>
          ) : null}
        </div>

        {user?.elearning?.benefits?.length ? (
          <div style={styles.benefitsGrid}>
            {user.elearning.benefits.map((b: any, i: number) => (
              <div key={i} className="up-card" style={{ ...styles.benefitCard, animationDelay: `${i * 0.05}s` }}>
                <div style={styles.benefitIcon}>
                  🎁
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>
                    {b.name || 'Бенефит'}
                  </div>
                  <div style={{ fontSize: 11, color: '#8b8fa8', marginTop: 2 }}>
                    Получен {new Date(b.claimedAt).toLocaleDateString('ru-RU')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.empty}>
            <Gift size={48} color="#f5576c" style={{ opacity: 0.4 }} />
            <h3 style={{ color: '#5a5f7a', marginTop: 12, fontWeight: 700 }}>
              Пока нет полученных бенефитов
            </h3>
            <p style={{ color: '#8b8fa8', fontSize: 13 }}>
              Заработайте баллы и обменяйте их на призы
            </p>
          </div>
        )}
      </div>
    </>
  );
};

// ═══ KPI КАРТОЧКА ═══
const KPI: React.FC<{
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  accent: string;
  bg: string;
}> = ({ icon, value, label, accent, bg }) => (
  <div
    className="up-kpi"
    style={{
      background: 'rgba(255, 255, 255, 0.55)',
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.7)',
      borderRadius: 22,
      padding: 20,
      boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: accent }} />
    <div style={{
      width: 40,
      height: 40,
      borderRadius: 12,
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 12,
    }}>
      {icon}
    </div>
    <div style={{
      fontSize: 26,
      fontWeight: 800,
      color: '#1a1a2e',
      lineHeight: 1.1,
      marginBottom: 2,
      letterSpacing: '-0.5px',
    }}>
      {value}
    </div>
    <div style={{
      fontSize: 11,
      color: '#8b8fa8',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>
      {label}
    </div>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 1000, margin: '0 auto' },
  hero: {
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.7)',
    borderRadius: 28,
    padding: 28,
    marginBottom: 28,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)',
  },
  heroBg: {
    position: 'absolute',
    top: -60,
    right: -60,
    width: 240,
    height: 240,
    borderRadius: '50%',
    background: GRADIENT_HERO,
    opacity: 0.12,
    filter: 'blur(40px)',
    pointerEvents: 'none',
  },
  heroContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    position: 'relative',
    zIndex: 1,
    flexWrap: 'wrap',
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: 28,
    background: GRADIENT_HERO,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 36,
    fontWeight: 800,
    letterSpacing: 1,
    boxShadow: '0 16px 40px rgba(102, 126, 234, 0.4)',
    border: '3px solid rgba(255,255,255,0.7)',
    flexShrink: 0,
  },
  avatarEditBtn: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: '3px solid #fff',
    cursor: 'pointer',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    transition: 'all 0.25s ease',
    zIndex: 2,
  },
  avatarImg: {
    width: 96,
    height: 96,
    borderRadius: 28,
    objectFit: 'cover',
    boxShadow: '0 16px 40px rgba(102, 126, 234, 0.4)',
    border: '3px solid rgba(255,255,255,0.7)',
    flexShrink: 0,
    display: 'block',
  },
  name: {
    fontSize: 26,
    fontWeight: 800,
    margin: 0,
    color: '#1a1a2e',
    letterSpacing: '-0.5px',
    wordBreak: 'break-word',
  },
  email: {
    color: '#8b8fa8',
    fontSize: 14,
    margin: '6px 0 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 500,
    wordBreak: 'break-word',
  },
  heroBadges: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  roleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    background: 'rgba(102, 126, 234, 0.15)',
    color: '#667eea',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  levelBar: { marginTop: 20, position: 'relative', zIndex: 1 },
  levelBarTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  levelBarTrack: {
    height: 8,
    background: 'rgba(102, 126, 234, 0.1)',
    borderRadius: 4,
    overflow: 'hidden',
  },
  levelBarFill: {
    height: '100%',
    background: GRADIENT_HERO,
    borderRadius: 4,
    transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 14,
    marginBottom: 32,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  sectionIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: GRADIENT_HERO,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3)',
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
    color: '#1a1a2e',
  },
  counter: {
    padding: '4px 12px',
    background: 'rgba(102, 126, 234, 0.12)',
    color: '#667eea',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
  },
  loadingBlock: {
    display: 'flex',
    alignItems: 'center',
    padding: 40,
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.4)',
    borderRadius: 20,
    marginBottom: 30,
  },
  empty: {
    textAlign: 'center',
    padding: 60,
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(20px)',
    borderRadius: 24,
    border: '1px dashed rgba(102,126,234,0.3)',
    marginBottom: 30,
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginBottom: 32,
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: 14,
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 18,
    border: '1px solid rgba(255, 255, 255, 0.6)',
    boxShadow: '0 4px 16px rgba(31, 38, 135, 0.05)',
  },
  resultTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: '#1a1a2e',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginBottom: 4,
  },
  resultMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: '#8b8fa8',
    fontWeight: 500,
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 12,
  },
  benefitCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 18,
    border: '1px solid rgba(255, 255, 255, 0.6)',
    boxShadow: '0 4px 16px rgba(31, 38, 135, 0.05)',
  },
  benefitIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    flexShrink: 0,
    boxShadow: '0 6px 16px rgba(245, 87, 108, 0.3)',
  },
};

export default UserProfile;
